import { chevronBackOutline, settingsOutline } from 'ionicons/icons';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router';

import { IonCol, IonGrid, IonHeader, IonIcon, IonRow, IonText, IonToolbar } from '@ionic/react';

import { TSButton } from '../../components/atomic/TSButton/TSButton';
import { useAppDispatch, useAppSelector } from '../../store';
import { ACCOUNT_COMPONENT, getAdvocateProfile, setCurrentComponent } from './AdvocateAccountSlice';
import { ADVOCATE_COMPONENT } from './AdvocateChatSlice';

type AdvocatePageTitle = 'account' | 'resources' | 'chat';

const profileHeaderConfigs = [
  {
    component: ACCOUNT_COMPONENT.PROFILE,
    prevComponent: undefined,
    title: 'generic.profile',
  },
  {
    component: ACCOUNT_COMPONENT.EDIT_PROFILE,
    prevComponent: ACCOUNT_COMPONENT.PROFILE,
    title: 'personalDetailsPage.title',
  },
  {
    component: ACCOUNT_COMPONENT.LIVED_EXPERIENCES,
    prevComponent: ACCOUNT_COMPONENT.EDIT_PROFILE,
    title: 'advocateChatCard.livedExperiences',
  },
  {
    component: ACCOUNT_COMPONENT.CHANGE_PASSWORD,
    prevComponent: ACCOUNT_COMPONENT.PROFILE,
    title: 'advocateChangePasswordPage.title',
  },
] as const;

const SettingsIconHeader = ({ advocateTab }: { advocateTab: AdvocatePageTitle }) => {
  const history = useHistory();

  return (
    <TSButton
      variant="icon"
      className="ion-no-margin ion-no-padding"
      onClick={() => history.push({ pathname: '/settings', state: { advocateTab } })}
    >
      <IonIcon slot="icon-only" icon={settingsOutline} />
    </TSButton>
  );
};

export const AdvocatePageHeader = ({ advocatePageTitle }: { advocatePageTitle: AdvocatePageTitle }) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const currentProfileComponent = useAppSelector((state) => state.advocateAccountSlice.currentComponent);
  const currentProfileHeaderConfig = profileHeaderConfigs.find(
    (config) => config.component === currentProfileComponent,
  );
  const advocateChatPageStatus = useAppSelector((state) => state.advocateChatSlice.advocateChatPageStatus);
  const hasGradientHeader = advocateChatPageStatus !== ADVOCATE_COMPONENT.CHAT;

  const showHeader = () => {
    switch (advocatePageTitle) {
      case 'chat':
        return (
          hasGradientHeader && (
            <IonHeader className="ion-no-border">
              <IonGrid className="ion-no-padding">
                <IonRow className="ion-justify-content-center ts-margin-top-1">
                  <IonCol className="ion-no-padding" sizeMd="6" sizeXl="4">
                    <IonToolbar className="pageToolbar">
                      <IonRow className="ion-justify-content-end">
                        <SettingsIconHeader advocateTab={advocatePageTitle} />
                      </IonRow>
                    </IonToolbar>
                  </IonCol>
                </IonRow>
              </IonGrid>
            </IonHeader>
          )
        );
      case 'account':
        return (
          currentProfileHeaderConfig && (
            <IonHeader className="ion-no-border">
              <IonGrid className="ion-no-padding">
                <IonRow className="ion-justify-content-center ts-margin-top-1">
                  <IonCol className="ion-no-padding" sizeMd="6" sizeXl="4">
                    <IonToolbar className="pageToolbar">
                      <IonRow className="ion-align-items-center ion-justify-content-between">
                        <IonCol size="2">
                          <IonRow className="ion-justify-content-start">
                            {currentProfileHeaderConfig.prevComponent && (
                              <TSButton
                                variant="clear-black"
                                onClick={() => {
                                  if (currentProfileHeaderConfig.prevComponent) {
                                    dispatch(setCurrentComponent(currentProfileHeaderConfig.prevComponent));
                                  }
                                  dispatch(getAdvocateProfile());
                                }}
                              >
                                <IonIcon slot="icon-only" icon={chevronBackOutline} />
                              </TSButton>
                            )}
                          </IonRow>
                        </IonCol>
                        <IonCol size="8">
                          <IonRow className="ion-justify-content-center">
                            <IonText className="title-medium-small text-bold">
                              {t(`${currentProfileHeaderConfig.title}`)}
                            </IonText>
                          </IonRow>
                        </IonCol>
                        <IonCol size="2">
                          <IonRow className="ion-justify-content-end">
                            <SettingsIconHeader advocateTab={advocatePageTitle} />
                          </IonRow>
                        </IonCol>
                      </IonRow>
                    </IonToolbar>
                  </IonCol>
                </IonRow>
              </IonGrid>
            </IonHeader>
          )
        );
      case 'resources':
        return (
          <IonHeader className="ion-no-border">
            <IonGrid className="ion-no-padding">
              <IonRow className="ion-justify-content-center ts-margin-top-1">
                <IonCol className="ion-no-padding" sizeMd="6" sizeXl="4">
                  <IonToolbar className="pageToolbar">
                    <IonRow className="ion-align-items-center ion-justify-content-between">
                      <IonCol size="2"></IonCol>
                      <IonCol size="8">
                        <IonRow className="ion-justify-content-center">
                          <IonText className="title-medium-small text-bold">{t('generic.resources')}</IonText>
                        </IonRow>
                      </IonCol>
                      <IonCol size="2">
                        <IonRow className="ion-justify-content-end">
                          <SettingsIconHeader advocateTab={advocatePageTitle} />
                        </IonRow>
                      </IonCol>
                    </IonRow>
                  </IonToolbar>
                </IonCol>
              </IonRow>
            </IonGrid>
          </IonHeader>
        );
      default:
        return;
    }
  };

  return showHeader();
};
